import { Flex, CryptoIcon, useToggle, FlexProps } from '@blockanalitica/ui';
import { useCallback, useEffect, useRef } from 'react';
import styled, { css } from 'styled-components';
import { useParams, useNavigate } from 'react-router';

export interface NetworkSelectorProps {
  networksMap: Record<string, Record<string, string>>;
}

const NetworkSelectorWrapper = styled.div`
  position: relative;
`;

const Header = styled(Flex)<FlexProps>`
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  font-weight: 500;
  flex: 1;
  box-sizing: border-box;
  height: 100%;

  ${(props) => css`
    border: solid 1px ${props.theme.colors.spark.gray90};
    background-color: ${props.theme.colors.spark.gray90};
    border-radius: ${props.theme.sizes['-3']};
    gap: ${props.theme.space['xs']};
    font-size: ${props.theme.sizes['-1']};
    padding: ${props.theme.space['2xs']};

    &:hover {
      background-color: ${props.theme.colors.spark.gray80};
    }
  `}
`;

const OptionList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  font-weight: 500;
  position: absolute;
  width: 100%;
  max-height: 150px;
  overflow-y: auto;
  z-index: 100;
  scrollbar-width: none;
  box-sizing: border-box;
  width: max-content;
  left: 50%;
  transform: translateX(-50%);

  ${(props) => css`
    border: 1px solid ${props.theme.colors.spark.gray90};
    background-color: ${props.theme.colors.spark.gray90};
    border-radius: ${props.theme.sizes['-4']};
    color: ${props.theme.colors.text};
    font-size: ${props.theme.sizes['-1']};
  `}
`;

const Option = styled(Flex)<FlexProps>`
  cursor: pointer;
  align-items: center;
  &:last-child {
    border: none;
  }

  ${(props) => css`
    border-bottom: solid 1px ${props.theme.colors.spark.gray80};
    background-color: ${props.theme.colors.spark.gray90};
    gap: ${props.theme.space['2xs']};
    padding: ${props.theme.space['2xs']};

    &:hover {
      background-color: ${props.theme.colors.spark.gray80};
    }
  `}
`;

export default function NetworkSelector({
  networksMap,
  ...rest
}: NetworkSelectorProps) {
  const navigate = useNavigate();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [isOpen, toggle] = useToggle();
  const { network } = useParams() as { network: string };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        toggle(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [toggle]);

  const onOptionClick = useCallback(
    (value: string) => {
      navigate(`/${value}`);
      toggle(false);
    },
    [toggle, navigate]
  );

  return (
    <NetworkSelectorWrapper {...rest} ref={dropdownRef}>
      <Header onClick={() => toggle()}>
        <CryptoIcon name={networksMap[network].icon} size="2" />
      </Header>

      {isOpen ? (
        <OptionList>
          {Object.entries(networksMap).map(([network, networkInfo]) => (
            <Option key={network} onClick={() => onOptionClick(network)}>
              <CryptoIcon name={networkInfo.icon} size="2" />
              {networkInfo.name}
            </Option>
          ))}
        </OptionList>
      ) : null}
    </NetworkSelectorWrapper>
  );
}
